import { Spinner } from 'flowbite-react';
import { useSearchParams } from 'react-router-dom';

import { MatchFrame } from '@/features/courts/components/kit/match-frame';
import { PeriodLabel } from '@/features/courts/components/kit/period-label';
import { ScoreBlock } from '@/features/courts/components/kit/score-block';
import { SetTally } from '@/features/courts/components/kit/set-tally';
import { TeamPair } from '@/features/courts/components/kit/team-pair';
import { useCourtMatch } from '@/features/courts/hooks/use-court-match';
import { useActionCable } from '@/lib/action-cable';
import type { Court } from '@/types/api';
import { setsSpec } from '@/types/sports/sets';

import { setCount, setNumber } from './set-count';

export const SetsDisplay = ({ court }: { court: Court }) => {
  const [searchParams] = useSearchParams();
  const cable = useActionCable();
  const { match } = useCourtMatch(court.id, cable, setsSpec);

  if (!match) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const { config, state } = match;
  const swapped = searchParams.has('inverted') ? !state.swapped : state.swapped;

  return (
    <div className="flex h-screen w-screen flex-col items-center justify-between overflow-hidden font-display leading-none">
      <MatchFrame
        variant="display"
        courtName={court.name}
        label={match.label}
        scheduledAt={match.scheduled_at}
      />

      <div className="flex flex-col items-center gap-[2vh]">
        <PeriodLabel
          current={setNumber(state)}
          total={setCount(config)}
          label="Set"
          variant="display"
        />
        <SetTally
          sets={state.sets}
          setsWon={state.scores}
          swapped={swapped}
          variant="display"
        />
      </div>

      <TeamPair
        swapped={swapped}
        className="border-t px-[5vw] py-[2vh]"
        home={
          <ScoreBlock
            team={match.home_team}
            score={state.current_set.home_team}
            variant="display"
          />
        }
        away={
          <ScoreBlock
            team={match.away_team}
            score={state.current_set.away_team}
            variant="display"
            reverse
          />
        }
      />
    </div>
  );
};
